import { BoolFilter, FilterList, MultiSelectSimpleFilter, SelectFilter, Sort } from "@dash/filters";

import { supportTicketSortFields, supportTicketSortOrders } from "@megaron/megarax-uri";
import type { SupportTicketSortField } from "@megaron/megarax-uri";
import { priorities, ticketCategories, ticketStatuses, ticketStatusLabels } from "@megaron/support-contracts";
import type { Priority, TicketCategory, TicketStatus } from "@megaron/support-contracts";

import { supportTicketCategoryLabels } from "../shared/supportTicketCategoryOptions";
import { getSupportTagOptions } from "../shared/supportTagSuggestions";
import type { SupportTicketsSortValue } from "./useSupportTicketsFilters";

export type SupportTicketsFilterValues = {
  searchText: string;
  status: TicketStatus[];
  priority: Priority[];
  category: TicketCategory[];
  tags: string[] | undefined;
  sort: SupportTicketsSortValue | undefined;
  myTickets: boolean | undefined;
  isRead: boolean | undefined;
};

const statusOptions = ticketStatuses.map((s) => ({ label: ticketStatusLabels[s], value: s }));

const priorityLabels: Record<Priority, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
  urgent: "Pilny",
};

const priorityOptions = priorities.map((p) => ({ label: priorityLabels[p] ?? p, value: p }));

const categoryOptions = ticketCategories.map((category) => ({
  label: supportTicketCategoryLabels[category],
  value: category,
}));

const sortOptions: { label: string; value: SupportTicketSortField }[] = [
  { label: "Data aktualizacji", value: "updatedAt" },
  { label: "Data utworzenia", value: "createdAt" },
  { label: "Priorytet", value: "priority" },
];

const isSupportTicketsSortValue = (value: unknown): value is SupportTicketsSortValue =>
  value !== null &&
  typeof value === "object" &&
  "field" in value &&
  "order" in value &&
  supportTicketSortFields.some((field) => field === value.field) &&
  supportTicketSortOrders.some((order) => order === value.order);

type Props = {
  values: SupportTicketsFilterValues;
  onChange: (patch: Partial<SupportTicketsFilterValues>) => void;
  className?: string;
};

export const SupportTicketsFilters: React.FC<Props> = ({ values, onChange, className }) => {
  return (
    <FilterList className={className}>
      <BoolFilter
        label="Moje zgłoszenia"
        value={values.myTickets}
        trueLabel="Tak"
        falseLabel="Nie"
        onChange={(v) => onChange({ myTickets: v })}
      />
      <BoolFilter
        label="Odczyt"
        value={values.isRead}
        trueLabel="Przeczytane"
        falseLabel="Nieprzeczytane"
        onChange={(v) => onChange({ isRead: v })}
      />
      <MultiSelectSimpleFilter
        label="Status"
        values={values.status}
        options={statusOptions}
        onChange={(v) => onChange({ status: v })}
      />
      <MultiSelectSimpleFilter
        label="Priorytet"
        values={values.priority}
        options={priorityOptions}
        onChange={(v) => onChange({ priority: v })}
      />
      <MultiSelectSimpleFilter
        label="Kategoria"
        values={values.category}
        options={categoryOptions}
        onChange={(v) => onChange({ category: v })}
      />
      <SelectFilter
        label="Tagi"
        initiallySelectedValues={values.tags ?? []}
        onSelectedChange={(options) =>
          onChange({ tags: options.length > 0 ? options.map((option) => option.value) : undefined })
        }
        search={async (searchText) => getSupportTagOptions(searchText, values.tags ?? [])}
        variant="multi-select"
      />
      <Sort
        type="button"
        label="Sortuj"
        value={{ field: values.sort?.field ?? "", order: values.sort?.order ?? "" }}
        options={sortOptions}
        onChange={(value) => onChange({ sort: isSupportTicketsSortValue(value) ? value : undefined })}
      />
    </FilterList>
  );
};

export const defaultSupportTicketsFilters: SupportTicketsFilterValues = {
  searchText: "",
  status: [],
  priority: [],
  category: [],
  tags: undefined,
  sort: { field: "updatedAt", order: "DESC" },
  myTickets: undefined,
  isRead: undefined,
};
